import type { ReadonlyDeep } from "type-fest";

import { useGlobal } from "./useGlobal.ts";

type SolvariEnv = ReadonlyDeep<{
  config: {
    envBE: "acceptance" | "develop" | "local" | "production" | "testing";
    envType: "local" | "production" | "staging" | "testing";
    lang: "fr" | "nl";
    localeCode: "be-FR" | "be-NL" | "dk-DA" | "fr-FR" | "nl-NL" | "se-SV";
    themeId: number | string | null;
    translationsTimestamp: number | null;
    urlPrefix: "" | "fr" | "nl";
  };
  integrations: {
    algolia: {
      applicationId: string;
      env: "dev" | "prd" | "stg";
      publicSearchApiKey: string;
    };
    growthbook: {
      clientKey: string;
    };
    pusher: {
      appId: string;
      cluster: string;
      key: string;
    };
    recaptcha?: {
      siteKey: string;
    };
    youShouldAsk: {
      chatId: Partial<
        | Record<
            "be-FR" | "be-NL" | "dk-DA" | "fr-FR" | "nl-NL" | "se-SV",
            { mainSite: string; mySolvari: string }
          >
        | undefined
      >;
    };
  };
  network: {
    admin: string;
    api: string;
    argus: string;
    auth: {
      base: string;
      baseWithPrefix: string;
    };
    client: {
      base: string;
      baseWithPrefix: string;
    };
    cms: string;
    pro: {
      base: string;
      baseWithPrefix: string;
    };
    static: string;
  };
}>;

declare global {
  var solvariEnv: SolvariEnv | undefined;
}

/**
 * Retrieves the global Solvari environment configuration.
 * Provides access to auth, config, integrations (Algolia, GrowthBook, etc.), and network endpoints.
 *
 * @returns The Solvari environment object with all configuration settings
 * @throws Error if solvariEnv is not defined
 * @example
 * const env = getEnv();
 * console.log(env.config.lang); // 'nl' | 'fr'
 * @remarks Call lazily (not at module top-level) to avoid SSR issues.
 */
function getEnv(): SolvariEnv {
  const env = useGlobal<SolvariEnv>("solvariEnv").get();
  if (!env) {
    throw new Error("Could not find solvariEnv variables on globalThis.");
  }
  return env;
}

export type { SolvariEnv };
export { getEnv };
